import { makeObservable, observable } from 'mobx';
import * as React from 'react';
import { observer } from 'mobx-react';
import { Notify } from '@shared/ui-native/Notify';
import { cs } from '@shared/utils/utils-classname';
import { ISavedStateComment, SavedStateCommentsService } from './SavedStateCommentsService';
import { DateTimeSpan } from '@shared/ui-react/UtilComponents';
import { Events } from '@shared/Events';
import { DrawingsDisplayOpenlayers } from '@shared/user-state/comments/DrawingsDisplayOpenlayers';
import { DrawingsDisplayCss } from '@shared/user-state/comments/DrawingsDisplayCss';
import { Button } from '@shared/ui-react/Button';

@observer
export class ScreenshotCommentsList extends React.Component<{
    commentListUuid: string;
    commentsOnly: boolean;
    isLegacy: boolean;
    inFreeDrawing: boolean;
    onCommentVisible: () => void;
    drawingsDisplay: IDrawingsDisplay;
    isPageType2D?: boolean;
    isLocked?: boolean;
}> {
    @observable comments: ISavedStateComment[] = [];
    @observable isLoading: boolean = false;
    @observable latestClickedComment: ISavedStateComment = undefined;
    wrapperEl: HTMLDivElement = null;

    constructor(props: {
        commentListUuid: string;
        commentsOnly: boolean;
        isLegacy: boolean;
        inFreeDrawing: boolean;
        onCommentVisible: () => void;
        drawingsDisplay: IDrawingsDisplay;
        isPageType2D: boolean;
        isLocked?: boolean;
    }) {
        super(props);
        makeObservable(this);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.commentListUuid !== this.props.commentListUuid) {
            this.props.drawingsDisplay.hideDrawings();
            this.fetch(this.props.commentListUuid);
        } else if (prevProps.drawingsDisplay !== this.props.drawingsDisplay) {
            this.props.drawingsDisplay.setComments(this.comments);
            this.props.drawingsDisplay.drawDrawings();
        }
    }

    componentDidMount() {
        this.fetch(this.props.commentListUuid);
        Events.on('map-view-changed', this.moveFromView);
    }

    componentWillUnmount() {
        this.props.drawingsDisplay.hideDrawings();
        Events.off('map-view-changed', this.moveFromView);
    }

    render() {
        if (!this.comments.length) {
            return (
                <div className='text-center' style={{ padding: '2em' }}>
                    {__('js_saved_views.no_comments')}
                </div>
            );
        }
        return (
            <div
                id='ss-comments-wrapper'
                style={{ display: 'block' }}
                className={cs({ 'overlay-loading': this.isLoading })}
                ref={el => {
                    this.wrapperEl?.removeEventListener('wheel', this.onThumbnailWheel);
                    this.wrapperEl = el;
                    el?.addEventListener('wheel', this.onThumbnailWheel, { passive: false });
                }}
            >
                {this.comments.map(c => {
                    const drawingsDisplay = this.props.drawingsDisplay;
                    const canDisplayDrawing = (drawingsDisplay instanceof DrawingsDisplayOpenlayers && c.lat) || (drawingsDisplay instanceof DrawingsDisplayCss && !c.lat);

                    const isAdmin = kuura.hasPrivilege('ORGANIZATION_ADMIN') || kuura.hasPrivilege('SUPER_USER');
                    const isAuthor = c.author.id === kuura.getUserId() || isAdmin;
                    const canDelete = isAuthor && !this.props.isLocked;

                    return (
                        <section key={c.id} className='ss-comment flex-row'>
                            {!this.props.commentsOnly && (
                                <>
                                    {canDelete && (
                                        <Button
                                            small
                                            subtle
                                            icon='iki-trash'
                                            iconSize={20}
                                            title={__('general.delete')}
                                            onClick={() => this.remove(c.id)}
                                            style={{ position: 'absolute', right: 0, top: 5, opacity: 0.7 }}
                                        />
                                    )}
                                    <div
                                        data-comment-id={c.id}
                                        className={cs('ss-comment-thumbnail', {
                                            'has-image': canDisplayDrawing && !!c.canvas,
                                            'is-visible': c.canvasVisible,
                                        })}
                                        style={{
                                            backgroundImage: canDisplayDrawing && c.canvas ? 'url(' + c.canvas + ')' : 'none',
                                            backgroundSize: c.lat ? 'contain' : 'cover',
                                        }}
                                    >
                                        {canDisplayDrawing && !!c.canvas && (
                                            <div className='ss-comment-thumbnail-actions'>
                                                <Button small subtle icon={c.canvasVisible ? 'iki-hide' : 'iki-show'} iconSize={20} onClick={() => this.onCommentClick(c.id)} />
                                                {drawingsDisplay instanceof DrawingsDisplayOpenlayers && (
                                                    <Button
                                                        small
                                                        subtle
                                                        icon='iki-fit-to-screen'
                                                        iconSize={20}
                                                        onClick={() => {
                                                            if (!c.canvasVisible) this.onCommentClick(c.id);
                                                            drawingsDisplay.panToComment(c.id);
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}

                            <div className='flex-column' style={{ marginRight: 30 }}>
                                <header>
                                    <div>
                                        {typeof c.author === 'string' ? c.author : c.author.username}{' '}
                                        <span className='fa08'>
                                            <DateTimeSpan timestamp={c.date} />
                                        </span>
                                    </div>
                                </header>
                                <article style={{ whiteSpace: 'pre-wrap' }}>{c.text}</article>
                            </div>
                        </section>
                    );
                })}
            </div>
        );
    }

    onThumbnailWheel = (e: WheelEvent) => {
        if (!e.shiftKey) return;

        const drawingsDisplay = this.props.drawingsDisplay;
        if (!(drawingsDisplay instanceof DrawingsDisplayOpenlayers)) return;

        const thumbnail = (e.target as HTMLElement).closest('.ss-comment-thumbnail') as HTMLElement;
        const comment = this.comments.find(c => c.id === Number(thumbnail?.dataset.commentId));
        if (!comment?.canvasVisible) return;

        e.preventDefault();

        const delta = e.deltaY || e.deltaX;
        const zoom = drawingsDisplay.offsetCommentZoom(comment.id, delta > 0 ? 0.1 : -0.1);
        if (zoom === null) return;

        console.log(`drawing: ${comment.id}, zoom: ${zoom}`);
    };

    moveFromView = () => {
        if (this.props.inFreeDrawing == false) {
            if (!this.props.isPageType2D) {
                this.comments.forEach(c => (c.canvasVisible = false));
                this.props.drawingsDisplay.hideDrawings();
            }
            this.latestClickedComment = undefined;
        }
    };

    onCommentClick = (id: number) => {
        if (this.props.commentsOnly) return;

        const comment = this.comments.find(c => c.id === id);
        if (!comment) return;
        this.latestClickedComment = comment;

        // comment will become visible, need to reset the view, but wait a bit so that view reset event is ignored

        const toggleCommentVisible = () => {
            comment.canvasVisible = !comment.canvasVisible;
            if (comment.canvasVisible) {
                this.props.drawingsDisplay.showComment(comment.id);
            } else {
                this.props.drawingsDisplay.hideComment(comment.id);
            }
        };

        if (comment.canvasVisible) {
            toggleCommentVisible();
        } else {
            if (!this.props.isPageType2D) this.props.onCommentVisible();
            setTimeout(toggleCommentVisible, 10);
        }
    };

    fetch = (id: number | string = this.props.commentListUuid) => {
        this.isLoading = true;
        const notVisibleComments = new Map();
        this.comments.forEach(c => {
            if (!c.canvasVisible && c.id !== this.latestClickedComment?.id) {
                notVisibleComments.set(c.id, false);
            }
        });

        return SavedStateCommentsService.fetch(id, this.props.isLegacy)
            .done(({ comments }) => {
                for (const c of comments) {
                    if (c.canvas) {
                        c.canvas = c.canvas === '' ? null : c.canvas.replace(/(?:\r\n|\r|\n)/g, '');
                        c.canvasVisible = !!c.canvas;
                        c.text = c.text || c.commentData || '';
                        c.text = c.text.replace('[click for drawing]', ''); // old versions have this string saved in db
                    }
                    if (notVisibleComments.has(c.id)) {
                        c.canvasVisible = false;
                    }
                }
                this.comments = comments;
                this.props.drawingsDisplay.setComments(comments);
                this.props.drawingsDisplay.drawDrawings();
            })
            .always(() => (this.isLoading = false));
    };

    remove = (id: number) => {
        if (confirm(__('general.delete') + ' ?')) {
            this.props.drawingsDisplay.hideDrawings();
            SavedStateCommentsService.remove(id, this.props.isLegacy)
                .done(() => {
                    this.comments.splice(
                        this.comments.findIndex(cmt => cmt.id === id),
                        1
                    );
                    Notify.success();
                    this.fetch(this.props.commentListUuid);
                    this.props.drawingsDisplay.drawDrawings();
                })
                .fail(Notify.error)
                .always(() => (this.isLoading = false));
        }
    };
}
